/**
 * IndexedDB utility for storing projects
 *
 * Provides a simple interface to save/load projects with images
 * using IndexedDB for larger storage capacity than localStorage.
 */

import { blobManager } from './blobManager';

const DB_NAME = 'EmberBoardDB';
const DB_VERSION = 1;
const STORE_NAME = 'projects';

interface StoredProject {
  id: string;
  name: string;
  data: string; // JSON string of project
  images: Record<string, string>; // Map of relativePath -> base64 data URL
  lastModified: number;
}

/**
 * Open the IndexedDB database
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

/**
 * Convert blob URL to base64 data URL
 */
async function blobUrlToBase64(blobUrl: string): Promise<string> {
  const response = await fetch(blobUrl);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Save a project to IndexedDB
 */
export async function saveProjectToDB(
  projectId: string,
  projectName: string,
  projectData: any, // Changed from object to any to access nodes/referenceNodes
  imageStore: Map<string, string>
): Promise<void> {
  try {
    const db = await openDB();

    // Collect all image paths that are actually used in the project
    const usedImagePaths = new Set<string>();

    // Add beat node images
    if (Array.isArray(projectData.nodes)) {
      for (const node of projectData.nodes) {
        if (node.data?.imagePath) {
          usedImagePaths.add(node.data.imagePath);
        }
      }
    }

    // Add reference node images
    if (Array.isArray(projectData.referenceNodes)) {
      for (const refNode of projectData.referenceNodes) {
        if (refNode.data?.imagePath) {
          usedImagePaths.add(refNode.data.imagePath);
        }
      }
    }

    console.log('[IndexedDB] Saving project...', {
      projectName,
      nodesCount: projectData.nodes?.length || 0,
      titleNodesCount: projectData.titleNodes?.length || 0,
      referenceNodesCount: projectData.referenceNodes?.length || 0,
      usedImagePaths: Array.from(usedImagePaths),
      imageStoreSize: imageStore.size,
      blobManagerSize: blobManager.getAll().size,
    });

    // Convert all blob URLs to base64 (only used images)
    const images: Record<string, string> = {};

    // Save images from imageStore (beat images, only if used)
    for (const [relativePath, blobUrl] of imageStore.entries()) {
      if (!usedImagePaths.has(relativePath)) {
        console.log('[IndexedDB] Skipping unused imageStore image:', relativePath);
        continue;
      }

      try {
        images[relativePath] = await blobUrlToBase64(blobUrl);
        console.log('[IndexedDB] Saved image from imageStore:', relativePath);
      } catch (error) {
        console.error(`Failed to convert image ${relativePath}:`, error);
        // Skip this image but continue with others
      }
    }

    // Save images from blobManager (reference images and node images, only if used)
    for (const [key, blobUrl] of blobManager.getAll().entries()) {
      if (!usedImagePaths.has(key)) {
        console.log('[IndexedDB] Skipping unused blobManager image:', key);
        continue;
      }

      try {
        images[key] = await blobUrlToBase64(blobUrl);
        console.log('[IndexedDB] Saved image from blobManager:', key);
      } catch (error) {
        console.error(`Failed to convert blob-managed image ${key}:`, error);
        // Skip this image but continue with others
      }
    }

    console.log('[IndexedDB] Total images saved:', Object.keys(images).length);

    const storedProject: StoredProject = {
      id: projectId,
      name: projectName,
      data: JSON.stringify(projectData),
      images,
      lastModified: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(storedProject);

      request.onsuccess = () => {
        console.log(`Project saved to IndexedDB: ${projectName}`);
        resolve();
      };

      request.onerror = () => {
        const error = request.error;
        console.error('IndexedDB save error:', error);

        // Check for quota exceeded error
        if (error && error.name === 'QuotaExceededError') {
          reject(new Error('Storage quota exceeded. Try removing some images or clearing old projects.'));
        } else {
          reject(error || new Error('Failed to save project to IndexedDB'));
        }
      };
    });
  } catch (error) {
    console.error('Failed to save project to IndexedDB:', error);
    throw error;
  }
}

/**
 * Load a project from IndexedDB
 */
export async function loadProjectFromDB(
  projectId: string
): Promise<{ projectData: object; images: Map<string, string> } | null> {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(projectId);

      request.onsuccess = () => {
        const result = request.result as StoredProject | undefined;

        if (!result) {
          resolve(null);
          return;
        }

        console.log('[IndexedDB] Loading project...', {
          projectName: result.name,
          imageCount: Object.keys(result.images || {}).length
        });

        try {
          // Validate and parse project data FIRST so we can count image references
          let projectData;
          try {
            projectData = JSON.parse(result.data);
            console.log('[IndexedDB] Project data loaded:', {
              nodesCount: projectData.nodes?.length || 0,
              titleNodesCount: projectData.titleNodes?.length || 0,
              referenceNodesCount: projectData.referenceNodes?.length || 0,
            });
          } catch (parseError) {
            console.error('Failed to parse project data:', parseError);
            reject(new Error('Corrupted project data'));
            return;
          }

          // Count how many nodes reference each image path
          // This is crucial for correct reference counting after reload
          const imageRefCounts = new Map<string, number>();
          if (projectData.nodes) {
            for (const node of projectData.nodes) {
              const imagePath = node.data?.imagePath;
              if (imagePath) {
                imageRefCounts.set(imagePath, (imageRefCounts.get(imagePath) || 0) + 1);
              }
            }
          }
          if (projectData.referenceNodes) {
            for (const node of projectData.referenceNodes) {
              const imagePath = node.data?.imagePath;
              if (imagePath) {
                imageRefCounts.set(imagePath, (imageRefCounts.get(imagePath) || 0) + 1);
              }
            }
          }
          console.log('[IndexedDB] Image reference counts:', Array.from(imageRefCounts.entries()));

          // Convert base64 images back to blob URLs
          const images = new Map<string, string>();
          for (const [relativePath, base64] of Object.entries(result.images || {})) {
            try {
              // Validate base64 format
              if (!base64 || typeof base64 !== 'string' || !base64.includes('base64,')) {
                console.warn(`Invalid base64 format for image: ${relativePath}`);
                continue;
              }

              // Convert base64 to blob URL
              const byteString = atob(base64.split(',')[1]);
              const mimeType = base64.match(/data:([^;]+);/)?.[1] || 'image/png';
              const arrayBuffer = new ArrayBuffer(byteString.length);
              const uint8Array = new Uint8Array(arrayBuffer);

              for (let i = 0; i < byteString.length; i++) {
                uint8Array[i] = byteString.charCodeAt(i);
              }

              const blob = new Blob([arrayBuffer], { type: mimeType });

              // Check if this is a blob-managed image (reference images, etc.)
              // Blob-managed images have keys like "reference-image:abc123" or "node-image:xyz789"
              if (relativePath.includes(':')) {
                // Store in blobManager with proper reference count
                const blobUrl = blobManager.create(blob, relativePath);
                images.set(relativePath, blobUrl);

                // Add extra refs if multiple nodes share this image
                // blobManager.create() sets ref count to 1, so add (count - 1) more refs
                const refCount = imageRefCounts.get(relativePath) || 1;
                for (let i = 1; i < refCount; i++) {
                  blobManager.addRef(relativePath);
                }
                console.log('[IndexedDB] Restored image to blobManager:', relativePath, 'refCount:', refCount);
              } else {
                // Store in imageStore (legacy beat images with relative paths)
                const blobUrl = URL.createObjectURL(blob);
                images.set(relativePath, blobUrl);
                console.log('[IndexedDB] Restored image to imageStore:', relativePath);
              }
            } catch (imageError) {
              console.error(`Failed to load image ${relativePath}:`, imageError);
              // Skip this image but continue with others
            }
          }

          console.log('[IndexedDB] Images restored:', images.size);

          // Log node IDs to help debug duplicate issues
          if (projectData.nodes && projectData.nodes.length > 0) {
            console.log('[IndexedDB] Beat node IDs:', projectData.nodes.map((n: any) => n.id));
          }
          if (projectData.referenceNodes && projectData.referenceNodes.length > 0) {
            console.log('[IndexedDB] Reference node IDs:', projectData.referenceNodes.map((n: any) => n.id));
          }

          resolve({
            projectData,
            images,
          });
        } catch (error) {
          console.error('Failed to process loaded project:', error);
          reject(error);
        }
      };

      request.onerror = () => {
        console.error('IndexedDB load error:', request.error);
        reject(request.error || new Error('Failed to load project from IndexedDB'));
      };
    });
  } catch (error) {
    console.error('Failed to load project from IndexedDB:', error);
    throw error;
  }
}

/**
 * Get all project IDs from IndexedDB
 */
export async function getAllProjectIds(): Promise<string[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAllKeys();

    request.onsuccess = () => resolve(request.result as string[]);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a project from IndexedDB
 */
export async function deleteProjectFromDB(projectId: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(projectId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}
